import React, { useMemo } from 'react';

import { createQueryString } from '@eika-infrastruktur/utils';
import { IconTypes } from '@eika/icon';
import { LinkCard, LinkPanel } from '@eika/link-panel';

import { useConfig } from '../../../defaultConfig';
import { useRecurringPayments } from '../../../fetchHooks/useRecurringPayments';
import type { Account } from '../../../generated-types';
import useTranslation, { format } from '../../../hooks/useTranslation';
import { paymentTrackIDs } from '../../../utils/gtm/trackIDs/payment';

interface AccountBSURecurringPaymentMessageProps {
    account?: Account;
}
const AccountBSURecurringPaymentMessage = ({ account }: AccountBSURecurringPaymentMessageProps) => {
    const { bsuDetails } = useTranslation();
    const { data: recurringPayments, isLoading: recurringPaymentsIsLoading } = useRecurringPayments();

    const bsuAccountNumber = account && account.accountNumber;
    const { recurringPaymentLink } = useConfig();
    const recurringBsuPayments = recurringPayments?.filter(
        (recurringPayment) => recurringPayment.toAccount.value === bsuAccountNumber?.value,
    );

    const linkUrl = useMemo(() => {
        const baseUrl = recurringPaymentLink && recurringPaymentLink.url;
        const params = createQueryString({
            bsuaccountnumber:
                recurringBsuPayments?.length === 0 && bsuAccountNumber ? bsuAccountNumber.value : undefined,
            recurringpaymentid: recurringBsuPayments?.length === 1 ? String(recurringBsuPayments[0].id) : undefined,
            locktoaccount: recurringBsuPayments && !(recurringBsuPayments?.length > 1) ? 'true' : undefined,
            redirecturl: window.location.href,
        });
        return `${baseUrl}${params}`;
    }, [bsuAccountNumber, recurringBsuPayments, recurringPaymentLink]);

    if (recurringPaymentsIsLoading) return null;

    if (!recurringBsuPayments || recurringBsuPayments.length === 0) {
        return (
            <LinkPanel
                url={linkUrl}
                title={bsuDetails.noRecurringPaymentInfoCardTitle}
                textOne={bsuDetails.noRecurringPaymentInfoCardText}
                data-track-id={paymentTrackIDs.recurring.bsu}
                data-track-linkname={bsuDetails.noRecurringPaymentInfoCardTitle}
                isInformation
                className="bsu-additional-info"
            />
        );
    }
    const recurringBsuPaymentsSum = recurringBsuPayments.reduce((n, { amount }) => n + amount, 0);

    return (
        <div>
            <LinkCard
                icon={IconTypes.SPAREGRIS_36}
                title={bsuDetails.hasRecurringPaymentInfoCardTitle}
                textOne={format(bsuDetails.hasRecurringPaymentInfoCardText, recurringBsuPaymentsSum)}
                data-track-id={paymentTrackIDs.recurring.bsu}
                data-track-linkname={bsuDetails.hasRecurringPaymentInfoCardTitle}
                url={linkUrl}
                className="bsu-additional-info"
            />
        </div>
    );
};

export default AccountBSURecurringPaymentMessage;
